/**
 * Clinic-defined merge tags. Clinics configure these in Engagement settings and
 * can then use `{{ template }}` (e.g. `{{ facebook_url }}`) in any template that
 * runs through the merge-tag replacement pipeline.
 *
 * Persisted on `clinic.settings.custom_merge_tags` as a JSON array. This is the
 * single source of truth for the shape across the dashboard; keep it in sync
 * with the equivalent helpers in the server, portal, and widget packages.
 */
export type CustomMergeTag = {
    /** The merge-tag key without braces, e.g. `"facebook_url"`. */
    template: string
    /** The value substituted for the tag, e.g. `"https://facebook.com/1234"`. */
    value: string
    /** Archived tags remain stored for reference but are not available for use. */
    is_archived?: boolean
}

/**
 * Merge-tag templates pre-seeded (empty-valued) in the settings form so clinics
 * are nudged toward the most common social links. These are still stored in the
 * same `custom_merge_tags` JSON — they are not special-cased anywhere else.
 */
export const DEFAULT_CUSTOM_MERGE_TAG_TEMPLATES = [
    'facebook_url',
    'instagram_url',
] as const

/**
 * Normalizes a template key so `"{{ facebook_url }}"` and `"facebook_url"` both
 * resolve to `"facebook_url"`. Also strips whitespace and any stray braces.
 */
export function normalizeMergeTagTemplate(template: string): string {
    return template.replace(/[{}]/g, '').trim()
}

/**
 * `@db.Json` columns sometimes arrive as a JSON-encoded string rather than a
 * parsed value. This parses strings (returning `undefined` on malformed JSON)
 * and passes non-strings through untouched.
 */
function coerceMaybeJsonString(value: unknown): unknown {
    if (typeof value !== 'string') return value

    try {
        return JSON.parse(value)
    } catch {
        return undefined
    }
}

/**
 * Parses the raw `custom_merge_tags` JSON value into a strongly-typed list,
 * dropping any entries that don't have a usable template key. The persisted
 * value is untyped JSON (it comes back from GraphQL as `any`), so this is the
 * validation boundary — callers should always go through here.
 */
export function parseCustomMergeTags(value: unknown): CustomMergeTag[] {
    // `@db.Json` columns can surface through GraphQL as an already-parsed array
    // or as a JSON-encoded string, so coerce strings before validating.
    const parsed = coerceMaybeJsonString(value)
    if (!Array.isArray(parsed)) return []

    const tags: CustomMergeTag[] = []
    for (const entry of parsed) {
        if (!entry || typeof entry !== 'object') continue

        const {
            template,
            value: tagValue,
            is_archived: isArchived,
        } = entry as Record<string, unknown>
        if (typeof template !== 'string') continue

        const normalizedTemplate = normalizeMergeTagTemplate(template)
        if (!normalizedTemplate) continue

        tags.push({
            template: normalizedTemplate,
            value: typeof tagValue === 'string' ? tagValue : '',
            is_archived: isArchived === true,
        })
    }

    return tags
}

/**
 * Builds a `{ [template]: value }` map from clinic custom merge tags, ready to
 * be spread into a template mapper. Later entries win on duplicate keys.
 */
export function getCustomMergeTagMap(value: unknown): Record<string, string> {
    const map: Record<string, string> = {}
    for (const tag of parseCustomMergeTags(value)) {
        if (tag.is_archived) continue
        map[tag.template] = tag.value
    }
    return map
}

/**
 * Reads custom merge tags off a clinic settings object. The generated GraphQL
 * types don't expose `custom_merge_tags` yet (codegen runs against a live
 * server), but the field is selected in the fragment and present at runtime, so
 * we read it structurally here.
 */
export function getAllCustomMergeTagsFromSettings(
    settings: unknown,
): CustomMergeTag[] {
    const customMergeTags = (
        settings as { custom_merge_tags?: unknown } | null | undefined
    )?.custom_merge_tags
    return parseCustomMergeTags(customMergeTags)
}

export function getCustomMergeTagsFromSettings(
    settings: unknown,
): CustomMergeTag[] {
    return getAllCustomMergeTagsFromSettings(settings).filter(
        tag => !tag.is_archived,
    )
}
